import { groupLastGameWordInsights, buildLastGameCellsFromGames } from "../utils/lastGameWordInsights";
import { computeLastGameMasteryDelta } from "../utils/lastGameMasteryDelta";
import {
  formatProgressDelta,
  getProgressDeltaClassName,
} from "../utils/gameProgressDelta";
import "./LastGameStatsPanel.css";
import "./GameProgressDelta.css";

function renderGradeLabel(grade) {
  const match = /^([A-F])([+-])$/.exec(grade);
  if (!match) return grade;

  return (
    <>
      <span className="last-game-stats-cell__letter">{match[1]}</span>
      <sup className="last-game-stats-cell__modifier">{match[2]}</sup>
    </>
  );
}

function LastGameWordRow({ insight }) {
  const cells = insight.last5Games?.length
    ? buildLastGameCellsFromGames(insight.last5Games)
    : Array.isArray(insight.cells)
      ? insight.cells
      : [];
  const masteryDelta = computeLastGameMasteryDelta(insight);
  const masteryClassName = getProgressDeltaClassName(masteryDelta);

  return (
    <div className="last-game-stats-word-row">
      <span className="last-game-stats-word">{insight.mot}</span>
      <span
        className={`last-game-stats-mastery progress-delta ${masteryClassName}`}
        aria-label={`Contribution maîtrise : ${formatProgressDelta(masteryDelta)}`}
      >
        {formatProgressDelta(masteryDelta)}
      </span>
      <div className="last-game-stats-cells" role="list" aria-label={`Historique de ${insight.mot}`}>
        {cells.map((cell, index) => {
          const isLatest = index === cells.length - 1;
          return (
          <span
            key={`${insight.mot}-${index}`}
            role="listitem"
            className={[
              "last-game-stats-cell",
              cell.result === "success"
                ? "last-game-stats-cell--success"
                : "last-game-stats-cell--fail",
              isLatest && "last-game-stats-cell--latest",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-label={
              isLatest
                ? `Dernière partie — ${
                    cell.result === "success"
                      ? cell.gradeAfter
                        ? `réussite, grade ${cell.gradeAfter}`
                        : "réussite"
                      : "échec"
                  }`
                : cell.result === "success"
                ? cell.gradeAfter
                  ? `Réussite, grade ${cell.gradeAfter}`
                  : "Réussite"
                : "Échec"
            }
          >
            {cell.gradeAfter ? renderGradeLabel(cell.gradeAfter) : null}
          </span>
          );
        })}
      </div>
    </div>
  );
}

export default function LastGameStatsPanel({ lastSession, wordInsights }) {
  const groups = groupLastGameWordInsights(wordInsights);
  const hasInsights = groups.some((group) => group.words.length > 0);

  if (!lastSession) {
    return (
      <div className="last-game-stats-panel">
        <p className="last-game-stats-empty">Vous n&apos;avez joué aucune partie pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="last-game-stats-panel">
      <header className="last-game-stats-header">
        <h2 className="last-game-stats-title">Dernière partie jouée</h2>
        <p className="last-game-stats-meta">
          {lastSession.levelName || `Niveau ${lastSession.level}`}
          {" — "}
          {lastSession.date}
          {lastSession.time ? ` ${lastSession.time}` : ""}
          {" — "}
          Score&nbsp;: <strong>{lastSession.score}</strong>/{lastSession.totalQuestions}
        </p>
      </header>

      {!hasInsights ? (
        <p className="last-game-stats-empty">Aucun mot analysable pour cette partie.</p>
      ) : (
        groups.map((group) =>
          group.words.length > 0 ? (
            <section key={group.id} className="last-game-stats-section">
              <h3 className="last-game-stats-section-title">{group.label}</h3>
              <div className="last-game-stats-section-body">
                {group.words.map((insight) => (
                  <LastGameWordRow key={insight.mot} insight={insight} />
                ))}
              </div>
            </section>
          ) : null
        )
      )}
    </div>
  );
}
